/**
 * Os widgets da conversa do VITA.
 *
 * Fonte visual: handoff/redesign/Objetivo.dc.html (§ 3 do
 * handoff/12-prompt-objetivo-vita.md). Cada step do onboarding desenha UM
 * destes; nenhum step mais monta controle do bootstrap.
 *
 * REGRA DESTA PASTA: widget não guarda estado de negócio e não sabe o que é
 * um "objetivo" ou um "peso". Ele recebe valor + callback e desenha. Quem
 * guarda o estado e grava em `setStepData` continua sendo o step — foi assim
 * que o motor do formulário ficou intacto enquanto a casca inteira mudou.
 */

import React from 'react';

import haptics from '../../../design/feedback/haptics';
import s from './Widgets.module.scss';

/* ── Ícones ───────────────────────────────────────────────── */

export function Check({ tamanho = 13 }) {
  return (
    <svg width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M20 6 9 17l-5-5"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── Opções (lista de escolha única) ──────────────────────── */

/**
 * @param {Array<{value:string,label:string,sub?:string}>} opcoes
 * @param {string} valor selecionado
 * @param {Function} onSelecionar recebe o value
 * @param {boolean} numeradas mostra o índice à esquerda (§ 3)
 */
export function Opcoes({ opcoes = [], valor, onSelecionar, numeradas = true, nome = 'opcao' }) {
  return (
    <div className={s.opcoes} role="radiogroup" aria-label={nome}>
      {opcoes.map((op, i) => {
        const ativo = valor === op.value;
        return (
          <button
            key={op.value}
            type="button"
            role="radio"
            aria-checked={ativo}
            className={`${s.opcao} ${ativo ? s.opcaoAtiva : ''}`}
            onClick={() => {
              haptics.selection();
              onSelecionar(op.value);
            }}
          >
            {numeradas && <span className={s.opcaoNum}>{i + 1}</span>}
            <span className={s.opcaoLabel}>{op.label}</span>
            {op.sub && <span className={s.opcaoSub}>{op.sub}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ── Chips (escolha múltipla) ─────────────────────────────── */

export function Chips({ opcoes = [], valores = [], onToggle }) {
  return (
    <div className={s.chips}>
      {opcoes.map((op) => {
        const ativo = valores.includes(op.value);
        return (
          <button
            key={op.value}
            type="button"
            aria-pressed={ativo}
            className={`${s.chip} ${ativo ? s.chipAtivo : ''}`}
            onClick={() => {
              haptics.selection();
              onToggle(op.value);
            }}
          >
            {ativo && <Check />}
            <span>{op.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Card de régua ────────────────────────────────────────── */

/**
 * A moldura da régua: número grande, unidade, linha de referência e o
 * `RulerPicker` (intocado) dentro. O botão "Confirmar" é opcional — nos
 * steps de peso/altura quem confirma é o CTA do rodapé, e um segundo botão
 * dentro do card daria dois "continuar" na mesma tela.
 */
export function CardRegua({ valor, unidade, referencia, vertical = false, children }) {
  return (
    <div className={`${s.cardRegua} ${vertical ? s.cardReguaV : ''}`}>
      <div className={s.reguaValor}>
        <span className={s.reguaNumero}>{valor}</span>
        <span className={s.reguaUnidade}>{unidade}</span>
      </div>
      {referencia && <span className={s.reguaRef}>{referencia}</span>}
      <div className={s.reguaTrilho}>{children}</div>
    </div>
  );
}

/* ── Dias da semana ───────────────────────────────────────── */

/* Os `value` são exatamente os rótulos que o gerador de plano já usa em
   `frequencia_dias` ("Seg", "Ter", … "Sab"). Um vocabulário só: o que o
   onboarding grava entra direto no payload da geração, sem tradução. */
export const DIAS_SEMANA = [
  { value: 'Seg', label: 'Seg' },
  { value: 'Ter', label: 'Ter' },
  { value: 'Qua', label: 'Qua' },
  { value: 'Qui', label: 'Qui' },
  { value: 'Sex', label: 'Sex' },
  { value: 'Sab', label: 'Sáb' },
  { value: 'Dom', label: 'Dom' },
];

export function Dias({ valores = [], onToggle }) {
  return (
    <div className={s.dias}>
      {DIAS_SEMANA.map((d) => {
        const ativo = valores.includes(d.value);
        return (
          <button
            key={d.value}
            type="button"
            aria-pressed={ativo}
            className={s.dia}
            onClick={() => {
              haptics.selection();
              onToggle(d.value);
            }}
          >
            <span className={`${s.diaRotulo} ${ativo ? s.diaRotuloAtivo : ''}`}>{d.label}</span>
            <span className={`${s.diaBola} ${ativo ? s.diaBolaAtiva : ''}`}>
              {ativo && <Check tamanho={14} />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Stepper de hora ──────────────────────────────────────── */

export function CardHora({ rotulo, valor, onMenos, onMais, grande = false }) {
  return (
    <div className={s.cardHora}>
      <span className={s.horaRotulo}>{rotulo}</span>
      <div className={s.horaLinha}>
        <button
          type="button"
          className={s.horaBotao}
          onClick={() => {
            haptics.selection();
            onMenos();
          }}
          aria-label="Uma hora antes"
        >
          −
        </button>
        <span className={`${s.horaValor} ${grande ? s.horaValorGrande : ''}`}>{valor}</span>
        <button
          type="button"
          className={s.horaBotao}
          onClick={() => {
            haptics.selection();
            onMais();
          }}
          aria-label="Uma hora depois"
        >
          +
        </button>
      </div>
    </div>
  );
}

/* ── Períodos do dia (manhã / tarde / noite) ──────────────── */

const CAMINHO_PERIODO = {
  Manhã:
    'M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10M12 1v2M12 21v2M4.2 4.2l1.4 1.4'
    + 'M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4',
  Tarde: 'M12 2v8M4.9 10.9l1.4 1.4M2 18h20M6 22h12M17.7 12.3l1.4-1.4',
  Noite: 'M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9',
};

export function Periodos({ lista = [], ativo, onSelecionar }) {
  return (
    <div className={s.periodos}>
      {lista.map((p) => {
        const on = ativo === p.nome;
        return (
          <button
            key={p.nome}
            type="button"
            aria-pressed={on}
            className={`${s.periodo} ${on ? s.periodoAtivo : ''}`}
            onClick={() => {
              haptics.selection();
              onSelecionar(p);
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d={CAMINHO_PERIODO[p.nome]} />
            </svg>
            <span className={s.periodoNome}>{p.nome}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ── Campo de texto ───────────────────────────────────────── */

export const Campo = React.forwardRef(function Campo(
  { valor, onChange, placeholder, multilinha = false, ...resto },
  ref
) {
  const Tag = multilinha ? 'textarea' : 'input';
  return (
    <Tag
      ref={ref}
      className={multilinha ? s.textarea : s.campo}
      value={valor}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      rows={multilinha ? 3 : undefined}
      {...resto}
    />
  );
});

/* ── Nota e link discreto ─────────────────────────────────── */

export function Nota({ children, alerta = false }) {
  return <span className={`${s.nota} ${alerta ? s.notaAlerta : ''}`}>{children}</span>;
}

export function LinkSuave({ children, onClick, ativo = false }) {
  return (
    <button
      type="button"
      className={`${s.link} ${ativo ? s.linkAtivo : ''}`}
      onClick={onClick}
      aria-pressed={ativo}
    >
      {children}
    </button>
  );
}

/* ── Card genérico da conversa ────────────────────────────── */

export function CardChat({ titulo, children, className = '' }) {
  return (
    <div className={`${s.card} ${className}`}>
      {titulo && <span className={s.cardTitulo}>{titulo}</span>}
      {children}
    </div>
  );
}
